import { intranetChatAttachmentPath, type IntranetChatPhoto } from '@gb-ticket/shared-types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API_BASE_URL } from '@/lib/api';
import {
  fetchIntranetChatPhotos,
  useIntranetChatPhotos,
} from '@/products/intranet/features/intranet/use-intranet-chat';

export const CHAT_PHOTOS_EMPTY = 'Dieser Chat hat noch keine Fotos.';

export function ChatPhotoGallery({
  open,
  conversationId,
  onClose,
  onReveal,
}: {
  open: boolean;
  conversationId: string;
  onClose: () => void;
  onReveal: (messageId: string) => void;
}) {
  const photos = useIntranetChatPhotos(conversationId, open);
  const [more, setMore] = useState<{
    id: string;
    items: IntranetChatPhoto[];
    cursor: string | null;
  }>({ id: '', items: [], cursor: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const active = more.id === conversationId;
  const extra = active ? more.items : [];
  const cursor = active ? more.cursor : (photos.data?.nextCursor ?? null);
  const items = [...(photos.data?.items ?? []), ...extra];

  async function loadMore() {
    if (!cursor || loadingMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const page = await fetchIntranetChatPhotos(conversationId, cursor);
      setMore({
        id: conversationId,
        items: [...extra, ...page.items],
        cursor: page.nextCursor,
      });
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fotos</DialogTitle>
        </DialogHeader>
        {photos.isPending ? (
          <p className="text-sm text-muted-foreground">Fotos werden geladen …</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{CHAT_PHOTOS_EMPTY}</p>
        ) : (
          <ul className="grid grid-cols-3 gap-2">
            {items.map((item) => (
              <li key={item.attachment.id} className="space-y-1">
                <a href={`${API_BASE_URL}${intranetChatAttachmentPath(item.attachment.id)}`}>
                  <img
                    src={`${API_BASE_URL}${intranetChatAttachmentPath(item.attachment.id)}`}
                    alt={item.attachment.filename}
                    className="aspect-square w-full rounded-md object-cover"
                  />
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={() => {
                    onReveal(item.messageId);
                    onClose();
                  }}
                >
                  Im Chat zeigen
                </Button>
              </li>
            ))}
          </ul>
        )}
        {cursor ? (
          <Button
            type="button"
            variant="outline"
            disabled={loadingMore}
            onClick={() => void loadMore()}
          >
            Ältere Fotos
          </Button>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
